'use strict';

/**
 * FieldCodeEditor -- the pict-section-code editor (CodeJar plus the section's built-in JSON /
 * JavaScript / HTML / CSS / SQL highlighting) embedded INSIDE the generic EntityList edit form, for any
 * descriptor field declared `T: 'code'`.
 *
 * It exists because some fields are not prose. A node's CapabilityJSON is the case that produced it
 * (WI #456): where a node's checkouts live is now maintained from the web UI instead of by SSH-ing in
 * to edit a file, and offering that as a bare textarea makes a person hand-balance braces in a box with
 * no line numbers and no way to see a typo until the node fails to resolve a workspace.
 *
 * Mirrors PictView-FieldMarkdownEditor exactly: mounts into a host div after the form renders, bound to
 * an AppData address, and writes its content back into the form's FormValues through the host
 * (PictView-EntityList.onCodeChanged) so the existing save() posts it with no special handling.
 *
 * pict-section-code and codejar already ride the client bundle (pict-section-content and pict-docuserve
 * both depend on the section), so this adds a view rather than a payload.
 */

const libCodeSection = require('pict-section-code');

/**
 * One editor instance, addressed at a host element and a host view.
 *
 * Parameterised for the same reason the markdown one is: a second surface that edits a code field
 * cannot share this instance, because each carries its own CodeJar state, its own content address and
 * its own host to call back into.
 */
function buildConfiguration(pOptions)
{
	let tmpOptions = pOptions || {};
	let tmpIdentifier = tmpOptions.ViewIdentifier || 'Plansheet-FieldCodeEditor';
	let tmpAddress = tmpOptions.HostAddress || '#EntityForm-CodeHost';
	let tmpDataKey = tmpOptions.DataKey || 'FieldCodeEditor';

	let tmpConfig = JSON.parse(JSON.stringify(libCodeSection.default_configuration));
	tmpConfig.ViewIdentifier = tmpIdentifier;
	tmpConfig.DefaultDestinationAddress = tmpAddress;
	tmpConfig.TargetElementAddress = tmpAddress;
	tmpConfig.CodeDataAddress = 'AppData.' + tmpDataKey + '.Code';
	tmpConfig.DataKey = tmpDataKey;
	// The view this editor reports its changes to.
	tmpConfig.HostViewIdentifier = tmpOptions.HostViewIdentifier || 'Plansheet-EntityList';
	// JSON rather than the section's javascript default: every code field in this app today is a
	// configuration blob, and a field can override it with `Language` on its descriptor entry.
	tmpConfig.Language = tmpOptions.Language || 'json';
	tmpConfig.LineNumbers = true;
	tmpConfig.ReadOnly = false;
	// The form controls when this mounts (after its own render), the same as the markdown field.
	tmpConfig.RenderOnLoad = false;
	tmpConfig.AutoRender = false;

	if (Array.isArray(tmpConfig.Renderables))
	{
		for (let i = 0; i < tmpConfig.Renderables.length; i++)
		{
			tmpConfig.Renderables[i].DestinationAddress = tmpAddress;
		}
	}
	return tmpConfig;
}

class PictViewFieldCodeEditor extends libCodeSection
{
	_host() { return this.pict.views[this.options.HostViewIdentifier || 'Plansheet-EntityList']; }

	/**
	 * Tell the host on every change, so the form's save path needs to know nothing about this editor.
	 *
	 * Best effort by design: a host that does not implement onCodeChanged (an editor mounted somewhere
	 * other than the entity form) simply keeps its content in its own AppData address.
	 */
	onCodeChanged()
	{
		if (typeof super.onCodeChanged === 'function') { super.onCodeChanged(); }
		let tmpHost = this._host();
		if (tmpHost && typeof tmpHost.onCodeChanged === 'function') { tmpHost.onCodeChanged(); }
	}

	// What the editor currently holds, however the section chooses to store it. Read through one
	// accessor so the host never reaches into the section's internals.
	getCode()
	{
		if (typeof this.getCodeContent === 'function') { return String(this.getCodeContent() || ''); }
		let tmpKey = this.options.DataKey || 'FieldCodeEditor';
		let tmpBucket = this.pict.AppData[tmpKey] || {};
		return String(tmpBucket.Code || '');
	}
}

module.exports = PictViewFieldCodeEditor;
module.exports.default_configuration = buildConfiguration();
module.exports.buildConfiguration = buildConfiguration;
